import {
  YES_NO,
  applicantSection,
  otherAttachments,
  proxyAttachments,
  representationSection,
} from "modules/ebiForms/common";
import { electronicDeliverySection } from "modules/ebiForms/definitions/sharedPlanningTaxes";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A3. Pismo dotyczące aktu planowania przestrzennego (wniosek do projektu, uwaga, wniosek o zmianę lub sporządzenie
 * planu ogólnego, MPZP albo uchwały krajobrazowej) — art. 8g–8i upzp. Formularz jest OBLIGATORYJNY (pismo złożone poza
 * nim pozostawia się bez rozpoznania, art. 8g ust. 2–3): wzór z rozporządzenia MRiT z 13.11.2023 r. (Dz.U. 2023
 * poz. 2509) — https://www.dziennikustaw.gov.pl/DU/2023/2509/D2023000250901.pdf, pobrany i odczytany 22.10.2026.
 * Odwzorowano pkt 2–9 (pkt 1 „organ” i 10 „podpis” zastępuje EBI); limit 1000 znaków w pkt 7.1 i 7.2.4 jak w przypisie 6.
 * Rodzaj pisma (pkt 2) jest wariantem w BE — jedno pismo w EBI ma jeden rodzaj (wzór pozwala zaznaczyć kilka; wtedy
 * petent składa osobne pisma). Pominięto akty województwa (pkt 3.4 audyt krajobrazowy, 3.5 PZPW) — organem jest
 * zarząd/marszałek województwa, nie gmina.
 */
const PARAMETERS: EbiCondition = {
  all: [
    { field: "rodzajPisma", in: ["wniosekDoProjektu", "uwaga", "wniosekOZmiane"] },
    { any: [{ field: "rodzajAktu", includes: "planOgolny" }, { field: "rodzajAktu", includes: "mpzp" }] },
  ],
};

const WHOLE_PARCEL = [
  { value: "tak", label: "tak — całość działki lub działek" },
  { value: "nie", label: "nie — część działki lub działek" },
];

const A3: IEbiFormDefinition = {
  code: "A3",
  version: 1,
  title: "Pismo dotyczące aktu planowania przestrzennego",
  category: EEbiFormCategory.Planning,
  office: EEbiFormOffice.Municipality,
  description:
    "Wniosek do projektu planu ogólnego lub miejscowego, uwaga w konsultacjach albo wniosek o sporządzenie lub zmianę planu — urzędowy formularz (art. 8g upzp).",
  keywords: ["mpzp", "plan miejscowy", "plan ogólny", "zmiana planu", "wniosek do planu", "uwaga", "konsultacje", "uchwała krajobrazowa", "reklamy"],
  organ: "Wójt / Burmistrz / Prezydent Miasta (o przystąpieniu do sporządzenia lub zmiany aktu rozstrzyga rada gminy)",
  legalBasis:
    "art. 8g–8i ustawy z dnia 27 marca 2003 r. o planowaniu i zagospodarowaniu przestrzennym; rozporządzenie Ministra Rozwoju i Technologii z dnia 13 listopada 2023 r. w sprawie wzoru formularza pisma dotyczącego aktu planowania przestrzennego",
  request:
    "Na podstawie art. 8g ustawy z dnia 27 marca 2003 r. o planowaniu i zagospodarowaniu przestrzennym składam pismo dotyczące aktu planowania przestrzennego wskazanego poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty; pełnomocnictwo — 17 zł.",
  deadlineLabel: "wg ogłoszenia",
  deadline:
    "Wnioski do projektu i uwagi — w terminie z ogłoszenia lub obwieszczenia o konsultacjach. Wniosek o sporządzenie lub zmianę aktu nie wszczyna postępowania — gmina rozpatruje go w ramach analizy aktualności planów (art. 32 upzp).",
  variantField: "rodzajPisma",
  variants: {
    wniosekDoProjektu: { title: "Wniosek do projektu aktu planowania przestrzennego" },
    uwaga: { title: "Uwaga do konsultowanego projektu aktu planowania przestrzennego", documentType: "Pismo" },
    wniosekOZmiane: { title: "Wniosek o zmianę aktu planowania przestrzennego" },
    wniosekOSporzadzenie: { title: "Wniosek o sporządzenie aktu planowania przestrzennego" },
  },
  sections: [
    {
      code: "rodzajPisma",
      title: "2. Rodzaj pisma",
      fields: [
        {
          code: "rodzajPisma",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "wniosekDoProjektu", label: "2.1. wniosek do projektu aktu" },
            { value: "uwaga", label: "2.2. uwagę do konsultowanego projektu aktu" },
            { value: "wniosekOZmiane", label: "2.3. wniosek o zmianę aktu" },
            { value: "wniosekOSporzadzenie", label: "2.4. wniosek o sporządzenie aktu" },
          ],
        },
      ],
    },
    {
      code: "rodzajAktu",
      title: "3. Rodzaj aktu planowania przestrzennego",
      description:
        "Można zaznaczyć więcej niż jeden akt. Jeśli pismo dotyczy dokumentu powiązanego z aktem (np. prognozy oddziaływania na środowisko), zaznacz akt, którego ten dokument dotyczy.",
      fields: [
        {
          code: "rodzajAktu",
          type: "checkboxes",
          label: "Pismo dotyczy",
          required: true,
          hint: "Audyt krajobrazowy i plan zagospodarowania przestrzennego województwa sporządza samorząd województwa — pismo w ich sprawie składa się do marszałka województwa.",
          options: [
            { value: "planOgolny", label: "3.1. planu ogólnego gminy" },
            { value: "mpzp", label: "3.2. miejscowego planu zagospodarowania przestrzennego, w tym zintegrowanego planu inwestycyjnego lub miejscowego planu rewitalizacji" },
            {
              value: "uchwalaKrajobrazowa",
              label:
                "3.3. uchwały ustalającej zasady i warunki sytuowania obiektów małej architektury, tablic reklamowych i urządzeń reklamowych oraz ogrodzeń, ich gabaryty, standardy jakościowe oraz rodzaje materiałów budowlanych, z jakich mogą być wykonane",
            },
          ],
        },
      ],
    },
    applicantSection("4. Dane składającego pismo"),
    {
      code: "tytulDoNieruchomosci",
      title: "4.1. Tytuł do nieruchomości",
      fields: [
        {
          code: "wlascicielNieruchomosci",
          type: "radio",
          label: "Czy składający pismo jest właścicielem lub użytkownikiem wieczystym nieruchomości objętej wnioskiem lub uwagą?",
          required: true,
          options: YES_NO,
          hint: "Gdy działa pełnomocnik — odpowiedz za mocodawcę.",
        },
      ],
    },
    { ...representationSection(), title: "6. Pełnomocnik — w czyim imieniu składasz pismo" },
    {
      code: "tresc",
      title: "7. Treść pisma",
      fields: [
        {
          code: "tresc",
          type: "textarea",
          label: "7.1. Treść",
          required: true,
          maxLength: 1000,
          hint: "Do 1000 znaków. Dłuższe uzasadnienie dołącz jako załącznik.",
        },
        {
          code: "dzialkiTresc",
          type: "repeat",
          label: "7.2. Szczegółowe informacje dotyczące treści pisma w odniesieniu do działek ewidencyjnych (nieobowiązkowo)",
          hint: "W przypadku wypełnienia uzupełnij każdą kolumnę wiersza.",
          itemLabel: "Wiersz",
          max: 30,
          fields: [
            { code: "nazwaAktu", type: "text", label: "7.2.1. Nazwa aktu planowania przestrzennego", maxLength: 300 },
            { code: "identyfikator", type: "text", label: "7.2.2. Identyfikator działki lub działek ewidencyjnych", maxLength: 300, placeholder: "np. 141201_1.0001.125/4" },
            { code: "calosc", type: "select", label: "7.2.3. Czy teren objęty pismem obejmuje całość działki lub działek ewidencyjnych", options: WHOLE_PARCEL },
            { code: "tresc", type: "textarea", label: "7.2.4. Treść", maxLength: 1000 },
          ],
        },
        {
          code: "dzialkiParametry",
          type: "repeat",
          label: "7.3. Szczegółowe informacje dotyczące działek ewidencyjnych oraz niektórych parametrów (nieobowiązkowo)",
          hint: "W przypadku wypełnienia uzupełnij każdą kolumnę wiersza.",
          visibleWhen: PARAMETERS,
          itemLabel: "Wiersz",
          max: 30,
          fields: [
            { code: "nazwaPlanu", type: "text", label: "7.3.1. Nazwa planu ogólnego gminy lub miejscowego planu zagospodarowania przestrzennego", maxLength: 300 },
            { code: "identyfikator", type: "text", label: "7.3.2. Identyfikator działki lub działek ewidencyjnych", maxLength: 300, placeholder: "np. 141201_1.0001.125/4" },
            { code: "calosc", type: "select", label: "7.3.3. Czy teren objęty pismem obejmuje całość działki lub działek ewidencyjnych", options: WHOLE_PARCEL },
            {
              code: "klasaPrzeznaczenia",
              type: "text",
              label: "7.3.4. Nazwa lub nazwy klasy przeznaczenia terenu (albo symbol lub symbole klasy przeznaczenia terenu)",
              maxLength: 200,
            },
            { code: "maxZabudowa", type: "number", label: "7.3.5. Maksymalny udział powierzchni zabudowy [%]", decimals: 2, min: 0, max: 100, width: "third" },
            { code: "maxWysokosc", type: "number", label: "7.3.6. Maksymalna wysokość zabudowy [m]", decimals: 2, min: 0, width: "third" },
            {
              code: "minBiologicznieCzynna",
              type: "number",
              label: "7.3.7. Minimalny udział powierzchni biologicznie czynnej [%]",
              decimals: 2,
              min: 0,
              max: 100,
              width: "third",
            },
          ],
        },
      ],
    },
    electronicDeliverySection("8. Oświadczenie w sprawie korespondencji elektronicznej i sposób odbioru"),
  ],
  attachments: [
    ...proxyAttachments(),
    {
      code: "graniceTerenu",
      label: "Określenie granic terenu w formie graficznej",
      hint: "Nieobowiązkowo — gdy pismo dotyczy części działki ewidencyjnej lub działek ewidencyjnych (PDF lub GML).",
    },
    {
      code: "uzasadnienie",
      label: "Uzasadnienie wykraczające poza limit 1000 znaków",
      hint: "Nieobowiązkowo.",
    },
    {
      ...otherAttachments(),
      hint: "Nieobowiązkowo. Dane osobowe innych osób niż składający pismo i pełnomocnik należy zanonimizować (ukryć).",
    },
  ],
};

export default A3;
